import clsx from "clsx";
import { forwardRef, HTMLProps } from "react";

const Label = ({
  children,
  text,
  className,
  labelTextClassName,
  ...rest
}: HTMLProps<HTMLLabelElement> & {
  text: string;
  labelTextClassName?: string;
}) => {
  return (
    <label className={clsx("flex! flex-col", className)} {...rest}>
      <span className={clsx("pb-3 text-xs font-semibold", labelTextClassName)}>{text}</span>
      {children}
    </label>
  );
};

const Group = forwardRef<HTMLDivElement, HTMLProps<HTMLDivElement>>(({ children, className, ...rest }, ref) => {
  return (
    <div
      ref={ref}
      className={clsx(
        "flex items-center rounded-sm group/field group",
        "ring-1 ring-border-action",
        "hover:ring-1 ring-border-action hover:ring-border-action-hover",
        "focus-within:ring-2 focus-within:outline-hidden focus-within:ring-border-action-focus",
        "data-[error=true]:ring-border-action-destructive  data-[error=true]:focus:ring-border-action-destructive-hover",
        "hover:focus-within:ring-2 hover:focus-within:ring-border-action-focus",
        "data-[disabled=true]:text-text-base-primary-disabled!",
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
});
Group.displayName = "Group";

const Prefix = forwardRef<HTMLDivElement, HTMLProps<HTMLDivElement>>(({ children, className, ...rest }, ref) => {
  return (
    <div
      ref={ref}
      className={clsx("text-sm p-2 justify-center items-center border-r border-border-action", className)}
      {...rest}
    >
      {children}
    </div>
  );
});

Prefix.displayName = "Prefix";

const Suffix = forwardRef<HTMLDivElement, HTMLProps<HTMLDivElement>>(({ children, className, ...rest }, ref) => {
  return (
    <div
      ref={ref}
      className={clsx("text-sm p-2 justify-center items-center border-l border-border-action", className)}
      {...rest}
    >
      {children}
    </div>
  );
});

Suffix.displayName = "Suffix";

const Input = forwardRef<HTMLInputElement, HTMLProps<HTMLInputElement>>(({ className, ...rest }, ref) => {
  return (
    <input
      ref={ref}
      className={clsx(
        "grow p-2 rounded-sm text-sm ring-1 border-none",
        "hover:ring-1 ring-border-action hover:ring-border-action-hover",
        "focus-within:ring-2 focus-within:outline-hidden focus-within:ring-border-action-focus",
        "hover:focus-within:ring-2 hover:focus-within:ring-border-action-focus",
        "data-[error=true]:ring-border-action-destructive  data-[error=true]:focus:ring-border-action-destructive-hover",
        "group-[.group]/field:rounded-l-none group-[.group]/field:ring-0! group-hover/field:ring-0! group-focus-within/field:ring-0!",
        "disabled:bg-background-input-disabled disabled:text-text-base-primary-disabled",
        className
      )}
      {...rest}
    />
  );
});

Input.displayName = "Input";

const Error = ({
  message,
  className,
  children,
  ...rest
}: HTMLProps<HTMLDivElement> & {
  message?: string;
}) => {
  return (
    <div data-testid="error" className={clsx("text-xs pt-2 text-text-status-destructive h-5", className)} {...rest}>
      {message ? message : children}
    </div>
  );
};

export const Field = { Input, Label, Error, Prefix, Suffix, Group };
